import React, { useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import useApp from "../../../Hooks/useApp";
import { hasGraphPermission, hasCardPermission } from "../../../Util/Permissions";
import useFuelTable from "../../../Features/Tables/FuelManagement/Provider/useTable";
import useMaintenanceTable from "../../../Features/Tables/Maintanance/Provider/useTable";
import useExpenseTable from "../../../Features/Tables/OtherExpenses/Provider/useTable"; 
import { useNavigate } from 'react-router-dom';

const ExpenseDashboard = () => {
  const nav = useNavigate()
  const { user } = useApp();
  const { docs: fuelDocs, isLoading: fuelLoading } = useFuelTable();
  const { docs: maintenanceDocs, isLoading: maintenanceLoading } = useMaintenanceTable();
  const { docs: expenseDocs, isLoading: expenseLoading } = useExpenseTable(); // Hook for Other Expenses

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const filterDocsByDate = (docs, dateField) => {
    if (!startDate || !endDate) return docs;
    const start = new Date(startDate);
    const end = new Date(endDate);
    return docs.filter((doc) => {
      const date = new Date(doc[dateField]);
      return date >= start && date <= end;
    });
  };

  const filteredFuelDocs = useMemo(
    () => filterDocsByDate(fuelDocs, "fuel_date"),
    [startDate, endDate, fuelDocs]
  );

  const filteredMaintenanceDocs = useMemo(
    () => filterDocsByDate(maintenanceDocs, "last_maintenance_date"),
    [startDate, endDate, maintenanceDocs]
  );

  const filteredExpenseDocs = useMemo(
    () => filterDocsByDate(expenseDocs, "expense_date"),
    [startDate, endDate, expenseDocs]
  );

  const totalFuelCost = useMemo(
    () => filteredFuelDocs.reduce((acc, doc) => acc + (Number(doc.cost) || 0), 0),
    [filteredFuelDocs]
  );

  const totalMaintenanceCost = useMemo(
    () => filteredMaintenanceDocs.reduce((acc, doc) => acc + (Number(doc.maintenance_cost) || 0), 0),
    [filteredMaintenanceDocs]
  );

  const totalOtherExpenses = useMemo(
    () => filteredExpenseDocs.reduce((acc, doc) => acc + (Number(doc.amount) || 0), 0),
    [filteredExpenseDocs]
  );

  const totalExpenses = useMemo(
    () => totalFuelCost + totalMaintenanceCost + totalOtherExpenses,
    [totalFuelCost, totalMaintenanceCost, totalOtherExpenses]
  );

  const fuelExpenseData = useMemo(() => {
    const monthlyData = {};
    filteredFuelDocs.forEach((doc) => {
      const date = new Date(doc.fuel_date);
      const monthYear = `${date.toLocaleString("default", { month: "short" })} ${date.getFullYear()}`;
      monthlyData[monthYear] = (monthlyData[monthYear] || 0) + (Number(doc.cost) || 0);
    });
    return Object.entries(monthlyData)
      .map(([month, fuelCost]) => ({ month, fuelCost }))
      .sort((a, b) => new Date(a.month) - new Date(b.month));
  }, [filteredFuelDocs]);

  const maintenanceExpenseData = useMemo(() => {
    const types = {};
    filteredMaintenanceDocs.forEach((doc) => {
      const type = doc.maintenance_type || "Other Maintenance";
      types[type] = (types[type] || 0) + (Number(doc.cost) || 0);
    });
    return Object.entries(types).map(([name, value]) => ({ name, value }));
  }, [filteredMaintenanceDocs]);

  const expenseBreakdownData = useMemo(() => [
    { name: "Fuel", value: totalFuelCost },
    { name: "Maintenance", value: totalMaintenanceCost },
    { name: "Other Expenses", value: totalOtherExpenses },
  ], [totalFuelCost, totalMaintenanceCost, totalOtherExpenses]);

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#AF19FF"];

  return (
    <div className="p-8 bg-light_grey ">
      <div className="flex gap-5 text-lg font-bold text-white">
        <div className="bg-blue-500 p-1 rounded-lg hover:bg-orange-500"
          onClick={()=> nav("/dashboard/finance")}
        >Finance </div>
        <div className="bg-blue-500 p-1 rounded-lg hover:bg-orange-500"
          onClick={()=> nav("/dashboard/finance/company-accounts")}
        >Company Accounts</div>
        <div className="bg-blue-500 p-1 rounded-lg hover:bg-orange-500"
          onClick={()=> nav("/dashboard/finance/statements")}
        > Statement </div>
        <div className="bg-blue-500 p-1 rounded-lg hover:bg-orange-500"
          onClick={()=> nav("/dashboard/finance/trucks")}
          > Truck/Trella</div>
      </div>
      <h1 className="text-4xl font-bold mb-8 text-primary">Finance Dashboard</h1>

      <div className="flex flex-wrap gap-4 mb-6 items-center">
        <label className="flex flex-col text-sm">
          From:
          <input type="date" className="p-2 border rounded" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>
        <label className="flex flex-col text-sm">
          To:
          <input type="date" className="p-2 border rounded" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {hasCardPermission(user, "total_fuel_expenses") && (
          <ExpenseCard title="Total Fuel Expenses" amount={totalFuelCost} color="text-secondary" />
        )}
        {hasCardPermission(user, "total_maintenance_expenses") && (
          <ExpenseCard title="Maintenance Expenses" amount={totalMaintenanceCost} color="text-yellow" />
        )}
        {hasCardPermission(user, "other_expenses") && (
          <ExpenseCard title="Other Expenses" amount={totalOtherExpenses} color="text-red" />
        )}
        {hasCardPermission(user, "total_expenses") && (
          <ExpenseCard title="Total Expenses" amount={totalExpenses} color="text-green" />
        )}
      </div>

      {hasGraphPermission(user, "fuel_expenses_over_time") && (
        <ChartBlock title="Fuel Expenses Over Time" loading={fuelLoading}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={fuelExpenseData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" stroke="#667080" />
              <YAxis stroke="#667080" />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="fuelCost" stroke="#154469" strokeWidth={2} activeDot={{ r: 8 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartBlock>
      )}

      {hasGraphPermission(user, "overall_expenses_breakdown") && (
        <ChartBlock title="Overall Expense Breakdown">
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={expenseBreakdownData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={150}
                fill="#82ca9d"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {expenseBreakdownData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartBlock>
      )}

      {hasGraphPermission(user, "expenses_breakdown_by_type") && (
        <ChartBlock title="Expense Breakdown by Type">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={expenseBreakdownData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="name" stroke="#667080" />
              <YAxis stroke="#667080" />
              <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, "Amount"]} />
              <Bar dataKey="value" fill="#154469" name="Amount" />
            </BarChart>
          </ResponsiveContainer>
        </ChartBlock>
      )}
    </div>
  );
};

// Small reusable components for cards and charts
const ExpenseCard = ({ title, amount, color }) => (
  <div className="bg-white shadow-md rounded-lg p-6">
    <h2 className="text-xl font-semibold mb-2">{title}</h2>
    <p className={`text-3xl font-bold ${color}`}>
      ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </p>
  </div>
);

const ChartBlock = ({ title, children, loading }) => (
  <div className="mb-8 bg-white shadow-md rounded-lg p-5">
    <h2 className="text-2xl font-semibold mb-4 text-primary">{title}</h2>
    {loading ? <p className="text-center">Loading...</p> : children}
  </div>
);

export default ExpenseDashboard;
